import { ReactNode, useRef, type ComponentProps } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";

type LegacyPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end"
  | "right-start"
  | "right-end"
  | "auto"
  | "auto-start"
  | "auto-end";

type TooltipSide = NonNullable<ComponentProps<typeof TooltipContent>["side"]>;
type TooltipAlign = NonNullable<ComponentProps<typeof TooltipContent>["align"]>;

interface CustomTooltipProps {
  children: React.ReactNode;
  label: ReactNode;
  /** Applied to the span that wraps the trigger (e.g. to stretch in a grid). */
  wrapperClassName?: string;
  placement?: LegacyPlacement;
  isDisabled?: boolean;
  isOpen?: boolean;
  openDelay?: number;
}

const toSideAlign = (
  placement: LegacyPlacement
): { side: TooltipSide; align: TooltipAlign } => {
  const [rawSide, rawAlign] = placement.split("-");
  const side: TooltipSide =
    rawSide === "bottom" || rawSide === "left" || rawSide === "right"
      ? rawSide
      : "top";
  const align: TooltipAlign =
    rawAlign === "start" ? "start" : rawAlign === "end" ? "end" : "center";
  return { side, align };
};

const CustomTooltip = ({
  children,
  label,
  wrapperClassName,
  placement = "top",
  isDisabled = false,
  isOpen,
  openDelay = 0
}: CustomTooltipProps) => {
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const { side, align } = toSideAlign(placement);
  const hasLabel = label !== null && label !== undefined && label !== "";

  const trigger = (
    <span
      ref={tooltipRef}
      className={wrapperClassName}
      onFocus={(e) => e.preventDefault()}
    >
      {children}
    </span>
  );

  if (isDisabled || !hasLabel) return trigger;

  return (
    <TooltipProvider delayDuration={openDelay}>
      <Tooltip open={isOpen}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          sideOffset={4}
          className="z-10000 whitespace-pre-line"
        >
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CustomTooltip;
